import { z } from "zod";
import { CUSTOM_FIELD_SUBJECTS, CUSTOM_FIELD_TYPES, ENTITY_TYPES, FIELD_RESTRICTIONS } from "../enums.ts";

const stableKey = z.string().regex(/^[a-z0-9][a-z0-9_\-.]{0,60}$/, "lowercase key: letters, digits, _ - .");

export const ReferenceValueInput = z.object({
  valueKey: stableKey,
  label: z.string().min(1).max(200),
  labelHe: z.string().max(200).nullable().optional(),
  order: z.number().int().min(0).optional(),
  colour: z.string().max(40).nullable().optional(),
  active: z.boolean().default(true),
  metadata: z.record(z.unknown()).default({}),
});
export type ReferenceValueInput = z.infer<typeof ReferenceValueInput>;

export const ReferenceValueUpdate = z.object({
  label: z.string().min(1).max(200).optional(),
  labelHe: z.string().max(200).nullable().optional(),
  order: z.number().int().min(0).optional(),
  colour: z.string().max(40).nullable().optional(),
  active: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const ReorderInput = z.object({ valueKeys: z.array(z.string()).min(1) });

export const SettingUpdateInput = z.object({
  value: z.unknown(),
  scope: z.enum(["global", "program", "company"]).default("global"),
  scopeId: z.string().nullable().optional(),
});

export const WorkflowStateInput = z.object({
  key: stableKey,
  label: z.string().min(1).max(200),
  order: z.number().int().min(0).optional(),
  stateType: z.enum(["open", "in_progress", "done", "closed"]).default("open"),
  isDefault: z.boolean().default(false),
  active: z.boolean().default(true),
});
export const WorkflowStateUpdate = z.object({
  label: z.string().min(1).max(200).optional(),
  order: z.number().int().min(0).optional(),
  stateType: z.enum(["open", "in_progress", "done", "closed"]).optional(),
  isDefault: z.boolean().optional(),
  active: z.boolean().optional(),
});

export const WorkflowTransitionInput = z.object({
  fromKey: z.string(),
  toKey: z.string(),
  allowedRoles: z.array(z.string()).default([]),
});

export const FieldVisibilityInput = z.object({
  entityType: z.enum(ENTITY_TYPES),
  fieldKey: z.string().min(1).max(100),
  visibleToRoles: z.array(z.string()).default([]),
  restrictTo: z.enum(FIELD_RESTRICTIONS).nullable().optional(),
  note: z.string().max(400).nullable().optional(),
});
export type FieldVisibilityInput = z.infer<typeof FieldVisibilityInput>;

export const CustomFieldDefinitionInput = z.object({
  subjectType: z.enum(CUSTOM_FIELD_SUBJECTS),
  key: z.string().regex(/^[a-z][a-z0-9_]{1,50}$/),
  label: z.string().min(1).max(200),
  fieldType: z.enum(CUSTOM_FIELD_TYPES),
  options: z.array(z.string()).nullable().optional(),
  required: z.boolean().default(false),
  displayOrder: z.number().int().min(0).default(0),
  helpText: z.string().max(400).nullable().optional(),
  section: z.string().max(80).nullable().optional(),
  active: z.boolean().default(true),
});
export type CustomFieldDefinitionInput = z.infer<typeof CustomFieldDefinitionInput>;

export const CustomFieldDefinitionUpdate = z.object({
  label: z.string().min(1).max(200).optional(),
  section: z.string().max(80).nullable().optional(),
  options: z.array(z.string()).nullable().optional(),
  required: z.boolean().optional(),
  displayOrder: z.number().int().min(0).optional(),
  helpText: z.string().max(400).nullable().optional(),
  active: z.boolean().optional(),
});

export const CustomFieldValuesInput = z.record(z.string(), z.unknown());
